import { createFileRoute, Link } from "@tanstack/react-router";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Reveal } from "@/components/Reveal";
import { useI18n } from "@/i18n/I18nProvider";
import { HELP_ARTICLES } from "@/data/help-articles";
import { brandedTitle, safeDesc } from "@/lib/seo-meta";

const META_TITLE = brandedTitle("Help Center | Patient & Provider Guides");
const META_DESC = safeDesc(
  "Get help using Heallexa. Find answers about searching providers, claiming a listing, Free vs Premium plans, AI search, and contacting our support team."
);

export const Route = createFileRoute("/help")({
 head: () => ({
 meta: [
 { title: META_TITLE },
 { name: "description", content: META_DESC },
 { property: "og:title", content: META_TITLE },
 { property: "og:description", content: META_DESC },
 { property: "og:url", content: "https://heallexa.com/help" },
 { property: "og:type", content: "website" },
 ],
 links: [{ rel: "canonical", href: "https://heallexa.com/help" }],
 scripts: [
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "FAQPage",
 mainEntity: FAQS.en.map((f) => ({
 "@type": "Question",
 name: f.q,
 acceptedAnswer: { "@type": "Answer", text: f.a },
 })),
 }),
 },
 ],
 }),
 component: HelpPage,
});

const FAQS = {
 en: [
 { q: "How do I find a provider?", a: "Use Normal Search to filter by specialty, location, and insurance, or switch to Ask Heallexa and describe what you need in plain language. You can also tap the microphone for voice search." },
 { q: "Is Heallexa free for patients?", a: "Yes. Searching, browsing, and viewing provider details are completely free for patients." },
 { q: "Are listings verified?", a: "Listings marked with a Verified badge have been claimed and confirmed by the provider. Other listings are sourced from public information." },
 { q: "I'm a provider, how do I claim my listing?", a: "Go to List your practice, search for your name, verify ownership, and choose Free or Premium." },
 { q: "What insurance plans are supported?", a: "Heallexa surfaces providers accepting major plans including Blue Cross Blue Shield, Aetna, UnitedHealthcare, Cigna, Humana, Kaiser, Medicare and more." },
 { q: "How do I report incorrect info?", a: "Open the provider profile and click Report incorrect info. Our team reviews every report." },
 ],
 es: [
 { q: "¿Cómo encuentro un profesional?", a: "Usa la Búsqueda normal para filtrar por especialidad, ubicación y seguro, o cambia a Pregunta a Heallexa y describe lo que necesitas en lenguaje natural. También puedes pulsar el micrófono para buscar por voz." },
 { q: "¿Heallexa es gratis para pacientes?", a: "Sí. Buscar, explorar y ver perfiles es totalmente gratuito para pacientes." },
 { q: "¿Las fichas están verificadas?", a: "Las fichas con la insignia Verificado han sido reclamadas y confirmadas por el profesional. Las demás se basan en información pública." },
 { q: "Soy profesional, ¿cómo reclamo mi ficha?", a: "Ve a Registra tu consultorio, busca tu nombre, verifica la propiedad y elige Gratis o Premium." },
 { q: "¿Qué seguros se admiten?", a: "Heallexa muestra profesionales que aceptan planes principales como Blue Cross Blue Shield, Aetna, UnitedHealthcare, Cigna, Humana, Kaiser, Medicare y más." },
 { q: "¿Cómo reporto información incorrecta?", a: "Abre el perfil del profesional y pulsa Reportar información incorrecta. Nuestro equipo revisa cada reporte." },
 ],
} as const;

function HelpPage() {
 const { t, lang } = useI18n();
 const faqs = FAQS[lang];
 return (
 <div className="min-h-screen bg-background pb-1">
 <Navbar />
 <header className="pt-32 md:pt-36 pb-16 border-b border-border bg-card">
 <div className="mx-auto max-w-[1200px] px-6">
 <p className="text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">Support</p>
 <h1 className="text-[clamp(2.25rem,5vw,3.75rem)] font-medium leading-[1.05] tracking-[-0.04em] text-foreground">
 {t.help.title}
 </h1>
 <p className="mt-6 text-base leading-relaxed text-muted-foreground max-w-[560px]">{t.help.subtitle}</p>
 </div>
 </header>

 <main className="mx-auto max-w-[1200px] px-6 py-16 sm:py-24">
 <section className="mb-16">
 <Reveal from="up" delay={60}>
 <div className="mb-8">
 <p className="text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">Guides</p>
 <h2 className="text-[clamp(1.8rem,3vw,2.5rem)] font-medium tracking-[-0.03em] leading-[1.15] text-foreground">Patient guides</h2>
 </div>
 </Reveal>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 {HELP_ARTICLES.map((a, idx) => (
 <Reveal key={a.slug} from="up" delay={Math.min(idx, 8) * 70} className="h-full">
 <Link
 to="/help/$slug"
 params={{ slug: a.slug }}
 className="group hover-lift cursor-pointer card-surface p-6 sm:p-7 flex flex-col h-full rounded-md"
 >
 <h3 className="text-[16px] font-semibold leading-tight text-foreground">{a.title}</h3>
 <p className="mt-2 text-[13.5px] text-muted-foreground line-clamp-2">{a.description}</p>
 <div className="mt-auto pt-4 border-t border-border/60 flex items-center justify-end text-[12.5px]">
 <span className="inline-flex items-center gap-1 font-medium text-primary group-hover:gap-2 transition-all hover:underline">
 Read guide <span aria-hidden>→</span>
 </span>
 </div>
 </Link>
 </Reveal>
 ))}
 </div>
 </section>

 <Reveal from="up" delay={60}>
 <div className="mb-8">
 <p className="text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">FAQ</p>
 <h2 className="text-[clamp(1.8rem,3vw,2.5rem)] font-medium tracking-[-0.03em] leading-[1.15] text-foreground">Frequently asked questions</h2>
 </div>
 </Reveal>
 <Reveal from="up" delay={60}>
 <div className="card-surface rounded-md divide-y divide-border">
 {faqs.map((f) => (
 <details key={f.q} className="group p-5">
 <summary className="cursor-pointer text-[14px] font-medium text-foreground list-none flex justify-between items-center">
 {f.q}
 <span className="text-muted-foreground group-open:rotate-45 transition-transform">+</span>
 </summary>
 <p className="mt-3 text-[13.5px] text-muted-foreground leading-relaxed">{f.a}</p>
 </details>
 ))}
 </div>
 </Reveal>

 <Reveal from="up" delay={60}>
 <div className="mt-10 card-surface p-10 text-center rounded-md">
 <p className="text-[14px] text-muted-foreground">{t.help.cant}</p>
 <p className="mt-1 text-[14px] font-medium text-foreground">{t.help.email} <a href="mailto:support@heallexa.com" className="text-primary hover:underline">support@heallexa.com</a></p>
 <p className="mt-4 text-[14px]">{t.help.provider} <Link to="/list-your-practice" className="text-primary hover:underline">{t.help.list}</Link></p>
 </div>
 </Reveal>
 </main>

 <FooterSection />
 </div>
 );
}
